import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';
import { useAuthenticatedSession, useBalance, useDistribution, useExhibitor, usePopup, useToast } from '../../hooks';
import { createDistribution, createDistributionByBalance } from '../../services';
import { addToLocalStorage, clearByModuleFromLocalStorage, clearFromLocalStorage, getAndClearFromLocalStorage, getFromLocalStorage } from '../../store';
import { CustomError, CustomResponse } from '../../dtos';
import { Button, ConfirmModal } from '../../components';
import { MainLayout } from '../../components/layouts';
import {
    ButtonExpand,
    CompleteDistribution,
    Header,
    RecipientModal,
    Recipients,
    Resume,
    Templates,
    Timeline
} from './components';
import { Container, Content, ButtonExpandRow, FooterBar } from './styles';

const DistributionPage: React.FC = () => {
    const [step, setStep] = useState<number>(getAndClearFromLocalStorage({
        module: 'distribution',
        key: 'step',
        defaultValue: 1
    }));
    const [showConfirmCancelModal, setShowConfirmCancelModal] = useState<boolean>(false);
    const [showRecipientModal, setShowRecipientModal] = useState<boolean>(false);
    const [customError, setCustomError] = useState<CustomError | null>(getFromLocalStorage({
        module: 'distribution',
        key: 'customError',
        defaultValue: null
    }));

    const navigate = useNavigate();
    const {
        exhibitor,
        voucherType,
        dueDate,
        template,
        recipients,
        availableAmount,
        distributedAmount,
        selectTemplate,
        clearDistributionState,
        addCreatedRecipients,
        refreshVoucherType,
        resetRecipientsAmount
    } = useDistribution();
    const { subtractDistributedAmount } = useExhibitor();
    const { subtractDistributedBalanceAmount } = useBalance();
    const { user, handleRequest, loadAllData } = useAuthenticatedSession();
    const { showToast } = useToast();
    const { closePopup, showPopup } = usePopup();

    const { state } = useLocation() as {
        state: {
            isBalance: boolean;
        };
    };
    const { isBalance } = state;

    useBeforeUnload(
        useCallback(() => {
            addToLocalStorage([
                {
                    module: 'distribution',
                    key: 'step',
                    data: step
                },
                {
                    module: 'distribution',
                    key: 'exhibitor',
                    data: exhibitor
                },
                {
                    module: 'distribution',
                    key: 'voucher_type',
                    data: voucherType
                },
                {
                    module: 'distribution',
                    key: 'template',
                    data: template
                },
                {
                    module: 'distribution',
                    key: 'recipients',
                    data: recipients
                }
            ]);
        }, [step, exhibitor, voucherType, template, recipients])
    );

    const closePopupAndClearLocalStorage = useCallback(() => {
        closePopup();
        clearByModuleFromLocalStorage('distribution');
    }, [closePopup]);

    const navigateToHome = useCallback(() => {
        navigate('/home', {
            replace: true
        });
        closePopupAndClearLocalStorage();
    }, [navigate, closePopupAndClearLocalStorage]);

    const navigateToMenuBalance = useCallback(() => {
        navigate('/balance', {
            replace: true
        });
        closePopupAndClearLocalStorage();
    }, [navigate, closePopupAndClearLocalStorage]);

    const navigateToStepOne = useCallback(() => {
        resetRecipientsAmount();
        setStep(1);
        closePopupAndClearLocalStorage();
    }, [resetRecipientsAmount, closePopupAndClearLocalStorage]);

    const navigateToStepTwo = useCallback(() => {
        selectTemplate(null);
        setStep(2);
        closePopupAndClearLocalStorage();
    }, [selectTemplate, closePopupAndClearLocalStorage]);

    useEffect(() => {
        if (!customError)
            return;

        addToLocalStorage({
            module: 'distribution',
            key: 'customError',
            data: customError
        });

        const { type, message, parameters } = customError;

        switch (type) {
            case 'SESSION_EXPIRED':
            case 'SESSION_INVALID':
            case 'UNKNOWN':
                clearFromLocalStorage({
                    module: 'distribution',
                    key: 'customError'
                });
                return;
            case 'VOUCHER_TYPE_NOT_FOUND':
            case 'VOUCHER_TYPE_BLOCKED':
            case 'VOUCHER_TYPE_NOT_LINKED':
            case 'VOUCHER_TYPE_LIMIT_ZERO':
            case 'DISTRIBUTION_INSUFFICIENT_REMAINING_QUOTA':
            case 'VOUCHERS_UNAVAILABLE':
                showPopup({
                    message: 'Este tipo de ingresso não está mais disponível para distribuição.',
                    description: 'Você será direcionado à home.',
                    handleOk: navigateToHome
                });
                break;
            case 'TEMPLATE_NOT_FOUND':
            case 'TEMPLATE_BLOCKED':
                showPopup({
                    message: 'O template escolhido não está mais disponível.',
                    description: 'Você será direcionado à etapa 2 para selecionar um novo template.',
                    handleOk: navigateToStepTwo
                });
                break;
            case 'DISTRIBUTION_FULL_QUOTA':
                showPopup({
                    message: 'Você atingiu o limite de distribuição deste tipo de ingresso neste mês. Escolha outro tipo de ingresso ou entre em contato com a Flix Media.',
                    description: 'Você será direcionado à home.',
                    handleOk: navigateToHome
                });
                break;
            case 'DISTRIBUTION_BATCH_BALANCE_OUT_OF_RANGE':
                showPopup({
                    message: 'Este tipo de ingresso não faz mais parte do saldo para distribuição e essa distribuição será cancelada.',
                    description: 'Você será direcionado ao menu de saldo.',
                    handleOk: navigateToMenuBalance
                });
                break;
            case 'RECIPIENT_LIMIT_AMOUNT_REACHED':
                showPopup({
                    message: 'O limite de ingressos por destinatário foi alterado para {0}. Atualize a sua distribuição.',
                    description: 'Você será direcionado à etapa 1 para alterar a quantidade.',
                    parameters,
                    handleOk: navigateToStepOne
                });
                break;
            default:
                showToast({
                    message,
                    type: 'error'
                });
                clearFromLocalStorage({
                    module: 'distribution',
                    key: 'customError'
                });
        }
    }, [
        navigate,
        showPopup,
        showToast,
        navigateToHome,
        navigateToMenuBalance,
        navigateToStepOne,
        navigateToStepTwo,
        customError
    ]);

    useEffect(() => {
        if (recipients.length < 1 && step > 1) {
            setStep(1);
            selectTemplate(null);
        }
    }, [selectTemplate, recipients.length, step]);

    const goBack = useCallback(() => {
        clearDistributionState(false);

        navigate(isBalance ? '/balance' : '/home', {
            replace: true
        });
    }, [clearDistributionState, navigate, isBalance]);

    const handleCloseButton = useCallback(() => {
        if (recipients.length > 0 || template !== null) {
            setShowConfirmCancelModal(true);
        } else {
            goBack();
        }
    }, [goBack, recipients.length, template]);

    const getPrevButtonLabel = useCallback(() => {
        switch (step) {
            case 1:
                return 'Cancelar';
            default:
                return 'Voltar';
        }
    }, [step]);

    const handlePrevButtonClick = useCallback(() => {
        if (step === 1) {
            handleCloseButton();
        } else {
            setStep(oldStep => oldStep - 1);
        }
    }, [handleCloseButton, step]);

    const getNextButtonLabel = useCallback(() => {
        switch (step) {
            case 1:
            case 2:
                return 'Continuar';
            default:
                return 'Distribuir';
        }
    }, [step]);

    const isNextButtonDisabled = useCallback(() => {
        if (availableAmount < 0)
            return true;

        switch (step) {
            case 1:
                return recipients.length < 1 || recipients.filter(({ amount }) => amount < 1).length > 0;
            case 2:
                return template === null;
            default:
                return false;
        }
    }, [availableAmount, step, recipients, template]);

    const handleNextButtonClick = useCallback(async () => {
        let response: CustomResponse = {
            success: true
        };

        if (step === 3) {
            if (!exhibitor || !voucherType || !template)
                throw new Error('The states exhibitor, voucherType and template cannot be null');

            if (isBalance) {
                if (!dueDate)
                    throw new Error('The state dueDate cannot be null');

                response = await handleRequest(async () => {
                    const distribution = await createDistributionByBalance({
                        dueDate,
                        voucherTypeId: voucherType.id,
                        templateId: template.id,
                        type: 'BALANCE',
                        recipients
                    });

                    addCreatedRecipients(distribution.distributionRecipients);
                });

                if (response.success)
                    subtractDistributedBalanceAmount(exhibitor, voucherType, distributedAmount);
            } else {
                response = await handleRequest(async () => {
                    const distribution = await createDistribution({
                        voucherTypeId: voucherType.id,
                        templateId: template.id,
                        type: 'DEFAULT',
                        recipients
                    });

                    addCreatedRecipients(distribution.distributionRecipients);
                });

                if (response.success)
                    subtractDistributedAmount(exhibitor, voucherType, distributedAmount);
            }

            if (response.success || response.error.type !== 'SESSION_EXPIRED') {
                const data = await loadAllData(user);

                if (!response.success) {
                    if (data) {
                        const { exhibitors, balances } = data;
                        refreshVoucherType(exhibitors, balances);
                    }

                    setCustomError(response.error);
                }
            }
        }

        if (response.success)
            setStep(oldStep => oldStep + 1);
    }, [
        subtractDistributedAmount,
        subtractDistributedBalanceAmount,
        handleRequest,
        addCreatedRecipients,
        loadAllData,
        refreshVoucherType,
        user,
        isBalance,
        step,
        exhibitor,
        voucherType,
        dueDate,
        template,
        recipients,
        distributedAmount
    ]);

    if (!exhibitor || !voucherType)
        return <Navigate to={isBalance ? "/balance" : "/home"} replace />

    return (
        <>
            {showConfirmCancelModal && (
                <ConfirmModal
                    title="Cancelar distribuição"
                    content="Deseja realmente cancelar o processo de distribuição de cortesias?"
                    cancelButtonLabel="Não"
                    confirmButtonLabel="Sim, cancelar"
                    handleClose={() => setShowConfirmCancelModal(false)}
                    handleConfirmCancel={goBack}
                />
            )}
            {showRecipientModal && (
                <RecipientModal
                    title="Novo destinatário"
                    handleClose={() => setShowRecipientModal(false)}
                />
            )}
            {step < 4 ? (
                <MainLayout
                    id="distribution"
                    title="Distribuição de cortesias"
                    showFloatingNavigationMenu={false}
                    handleCloseButton={handleCloseButton}
                >
                    <Header />
                    <Container>
                        <Content>
                            <Timeline
                                firstComplete={step > 1}
                                secondComplete={step > 2}
                                thirdComplete={step > 3}
                            />
                            {step === 1 && (
                                <>
                                    <Recipients />
                                    {availableAmount > 0 && (
                                        <ButtonExpandRow >
                                            <ButtonExpand
                                                id="distribution.addrecipientbutton"
                                                onClick={() => setShowRecipientModal(true)}
                                            />
                                        </ButtonExpandRow>
                                    )}
                                </>
                            )}
                            {step === 2 && <Templates />}
                            {step === 3 && <Resume />}
                        </Content>
                        <FooterBar>
                            <Button
                                id="distribution.prevbutton"
                                outlined
                                onClick={handlePrevButtonClick}
                            >
                                {getPrevButtonLabel()}
                            </Button>
                            <Button
                                id="distribution.nextbutton"
                                disabled={isNextButtonDisabled()}
                                onClick={handleNextButtonClick}
                            >
                                {getNextButtonLabel()}
                            </Button>
                        </FooterBar>
                    </Container>
                </MainLayout>
            ) : (
                <CompleteDistribution
                    isBalance={isBalance}
                    resetDistributionLayout={() => setStep(1)}
                />
            )}

        </>
    );
};

export { DistributionPage };
